import { Component } from '@theme/component';

const HANDLE = /^[a-z0-9][a-z0-9-]{0,200}$/;
const KEYS = { wishlist: 'mtq:wishlist:v1', compare: 'mtq:compare:v1' };
const readList = (kind) => {
  try {
    const value = JSON.parse(localStorage.getItem(KEYS[kind]) || '[]');
    return Array.isArray(value) ? [...new Set(value.filter((s) => typeof s === 'string' && HANDLE.test(s)))].slice(0, kind === 'compare' ? 4 : 200) : [];
  } catch { return []; }
};
const element = (tag, className, text) => {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
};
const tools = () => document.querySelector('mtq-tools');
const label = (key) => tools()?.labels?.[key] || key;
const root = () => tools()?.dataset.root || '/';
const productURL = (handle) => `${root()}products/${encodeURIComponent(handle)}`;
const money = (cents) => new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: tools()?.dataset.currency || 'PKR' }).format(Number(cents) / 100);

class MTQTools extends Component {
  labels = {};
  controller;
  observer;
  timer;
  recognition;
  connectedCallback() {
    super.connectedCallback();
    this.controller?.abort();
    this.controller = new AbortController();
    try { this.labels = JSON.parse(this.querySelector('[data-mtq-labels]')?.textContent || '{}'); } catch { this.labels = {}; }
    const signal = this.controller.signal;
    document.addEventListener('click', this.onClick, { capture: true, signal });
    window.addEventListener('storage', this.refresh, { signal });
    this.observer?.disconnect();
    this.observer = new MutationObserver((records) => {
      if (records.some((r) => r.addedNodes.length)) this.refresh();
    });
    this.observer.observe(document.body, { childList: true, subtree: true });
    this.refresh();
  }
  updatedCallback() { super.updatedCallback(); this.refresh(); }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.controller?.abort();
    this.observer?.disconnect();
    clearTimeout(this.timer);
    this.recognition?.abort();
  }
  refresh = () => {
    for (const kind of ['wishlist', 'compare']) {
      const handles = readList(kind);
      document.querySelectorAll(`[data-mtq-count="${kind}"]`).forEach((node) => {
        const value = String(handles.length);
        if (node.textContent !== value) node.textContent = value;
      });
      document.querySelectorAll(`[data-mtq-action="${kind}"]`).forEach((node) => node.setAttribute('aria-pressed', String(handles.includes(node.dataset.productHandle))));
    }
    if (window.SpeechRecognition || window.webkitSpeechRecognition) document.querySelectorAll('[data-mtq-action="voice"]').forEach((node) => { node.hidden = false; });
  };
  announce(message) {
    const status = this.querySelector('.mtq-toast');
    if (!status) return;
    clearTimeout(this.timer);
    status.textContent = message;
    status.hidden = false;
    this.timer = setTimeout(() => { status.hidden = true; }, 4200);
  }
  save(kind, handles) {
    try { localStorage.setItem(KEYS[kind], JSON.stringify(handles)); }
    catch { this.announce(label('storage_unavailable')); return false; }
    this.refresh();
    document.dispatchEvent(new CustomEvent('mtq:state', { detail: { kind } }));
    return true;
  }
  onClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('[data-mtq-action]');
    const tagLink = event.target.closest('a[data-mtq-tag-link]');
    if (tagLink && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
      event.preventDefault();
      const target = new URL(tagLink.href, window.location.origin);
      target.search = window.location.search;
      target.searchParams.delete('page');
      window.location.assign(target.href);
      return;
    }
    if (!button) return;
    const action = button.dataset.mtqAction;
    const handle = button.dataset.productHandle;
    if (!['wishlist', 'compare', 'remove-wishlist', 'remove-compare', 'remove-shared', 'voice', 'share', 'apply-filters'].includes(action)) return;
    event.preventDefault();
    event.stopPropagation();
    if (action === 'apply-filters') {
      const form = button.closest('form');
      if (!form) return;
      const target = new URL(window.location.href);
      const params = new URLSearchParams(new FormData(form));
      for (const key of [...params.keys()]) if (!params.get(key)?.trim()) params.delete(key);
      params.delete('page');
      const current = new URLSearchParams(window.location.search);
      for (const key of ['q', 'type', 'options[prefix]', 'sort_by']) if (!params.has(key) && current.has(key)) params.set(key, current.get(key));
      target.search = params.toString();
      window.location.assign(target.href);
      return;
    }
    if (action === 'voice') { this.startVoice(button); return; }
    if (action === 'share') { this.share(); return; }
    if (!handle || !HANDLE.test(handle)) return;
    if (action === 'remove-shared') { button.closest('mtq-catalog-page')?.removeShared(handle); return; }
    const kind = action.replace('remove-', '');
    const current = readList(kind);
    const exists = current.includes(handle);
    const remove = exists || action.startsWith('remove-');
    if (!remove && kind === 'compare' && current.length >= 4) { this.announce(label('compare_limit')); return; }
    const next = remove ? current.filter((s) => s !== handle) : [...current, handle];
    if (this.save(kind, next)) this.announce(label(remove ? 'removed' : kind === 'wishlist' ? 'saved' : 'compare_added'));
  };
  startVoice(button) {
    const Recognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Recognition) return;
    this.recognition?.abort();
    const form = button.closest('form');
    const input = form?.querySelector('input[name="q"]');
    if (!form || !input) return;
    const recognition = new Recognition();
    this.recognition = recognition;
    recognition.lang = form.querySelector('[data-mtq-voice-language]')?.value || document.documentElement.lang || 'en-US';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognition.onresult = (event) => {
      const transcript = event.results?.[0]?.[0]?.transcript;
      if (transcript) { input.value = transcript; form.requestSubmit(); }
    };
    recognition.onerror = () => this.announce(label('voice_error'));
    recognition.onend = () => { button.setAttribute('aria-pressed', 'false'); };
    button.setAttribute('aria-pressed', 'true');
    this.announce(label('listening'));
    try { recognition.start(); } catch { button.setAttribute('aria-pressed', 'false'); this.announce(label('voice_error')); }
  }
  async share() {
    const params = new URLSearchParams(window.location.search);
    const handles = params.has('products') ? (params.get('products') || '').split(',').filter((h) => HANDLE.test(h)).slice(0, 4) : readList('compare');
    if (!handles.length) { this.announce(label('empty_compare')); return; }
    const url = new URL(`${root()}pages/compare`, window.location.origin);
    url.searchParams.set('products', handles.join(','));
    try { await navigator.clipboard.writeText(url.href); this.announce(label('copied')); }
    catch { this.announce(`${label('copy_error')} ${url.href}`); }
  }
}

class MTQCatalogPage extends Component {
  controller;
  request;
  renderVersion = 0;
  connectedCallback() {
    super.connectedCallback();
    this.controller?.abort();
    this.controller = new AbortController();
    document.addEventListener('mtq:state', this.render, { signal: this.controller.signal });
    window.addEventListener('storage', this.render, { signal: this.controller.signal });
    window.addEventListener('popstate', this.render, { signal: this.controller.signal });
    this.querySelector('[data-mtq-finder]')?.addEventListener('submit', this.find, { signal: this.controller.signal });
    this.render();
  }
  disconnectedCallback() { super.disconnectedCallback(); this.controller?.abort(); this.request?.abort(); }
  removeShared(handle) {
    const url = new URL(window.location.href);
    const list = (url.searchParams.get('products') || '').split(',').filter((h) => HANDLE.test(h) && h !== handle).slice(0, 4);
    url.searchParams.set('products', list.join(','));
    window.history.replaceState({}, '', url.href);
    this.render();
  }
  find = (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    const maximumInput = form.querySelector('[name="filter.v.price.lte"]');
    if (maximumInput) maximumInput.min = '0';
    const target = new URL(String(data.get('department') || `${root()}collections/all`), window.location.origin);
    if (target.origin !== window.location.origin) return;
    const style = String(data.get('style') || '');
    if (style && HANDLE.test(style)) target.pathname = `${target.pathname.replace(/\/$/, '')}/${style}`;
    const min = String(data.get('filter.v.price.gte') || '');
    const max = String(data.get('filter.v.price.lte') || '');
    if (min && Number(min) >= 0) target.searchParams.set('filter.v.price.gte', min);
    if (max && Number(max) >= 0) target.searchParams.set('filter.v.price.lte', max);
    if (min && max && Number(min) > Number(max)) {
      const input = form.querySelector('[name="filter.v.price.lte"]');
      input.min = min; input.reportValidity(); return;
    }
    window.location.assign(target.href);
  };
  render = async () => {
    const view = this.dataset.view;
    if (view === 'finder') return;
    const container = this.querySelector('[data-mtq-items]');
    if (!container) return;
    const params = new URLSearchParams(window.location.search);
    const shared = view === 'compare' && params.has('products');
    const handles = shared ? [...new Set((params.get('products') || '').split(',').filter((h) => HANDLE.test(h)))].slice(0, 4) : readList(view);
    this.request?.abort();
    this.request = new AbortController();
    const version = ++this.renderVersion;
    container.setAttribute('aria-busy', 'true');
    if (!handles.length) { this.empty(container, label(view === 'compare' ? 'empty_compare' : 'empty_wishlist')); return; }
    const results = await Promise.allSettled(handles.map(async (handle) => {
      const response = await fetch(`${productURL(handle)}.js`, { signal: this.request.signal, headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Product unavailable');
      const product = await response.json();
      if (!product?.handle || !HANDLE.test(product.handle)) throw new Error('Invalid product');
      return product;
    }));
    if (!this.isConnected || version !== this.renderVersion) return;
    const products = results.flatMap((r) => r.status === 'fulfilled' ? [r.value] : []);
    container.replaceChildren();
    if (!products.length) {
      this.empty(container, label('unavailable'));
      const removeRow = element('div', 'mtq-tools-page__bar');
      for (const handle of handles) {
        const remove = element('button', 'mtq-text-link', `${label('remove')}: ${handle.replace(/-/g, ' ')}`);
        remove.type = 'button'; remove.dataset.mtqAction = shared ? 'remove-shared' : `remove-${view}`; remove.dataset.productHandle = handle; removeRow.append(remove);
      }
      container.append(removeRow); return;
    }
    if (products.length !== handles.length) container.append(element('p', 'mtq-tools-page__intro', label('unavailable')));
    if (view === 'compare') this.comparison(container, products, shared);
    else {
      const grid = element('div', 'mtq-tools-page__grid');
      for (const product of products) grid.append(this.card(product, view, false));
      container.append(grid);
    }
    container.setAttribute('aria-busy', 'false');
  };
  empty(container, text) {
    const panel = element('div', 'mtq-empty');
    panel.append(element('p', '', text));
    const link = element('a', 'mtq-button', label('browse'));
    link.href = `${root()}collections/all`;
    panel.append(link);container.replaceChildren(panel);container.setAttribute('aria-busy', 'false');
  }
  card(product, view, shared) {
    const card = element('article', 'mtq-saved-product');
    const imageLink = element('a', 'mtq-saved-product__image');
    imageLink.href = productURL(product.handle); imageLink.setAttribute('aria-label', product.title);
    const source = product.featured_image || product.images?.[0];
    if (source) {
      const url = new URL(typeof source === 'string' ? source : source.src, window.location.origin);
      if (['https:', 'http:'].includes(url.protocol)) {
        url.searchParams.set('width', '600');
        const image = element('img');image.src=url.href;image.alt=product.title;image.width=600;image.height=600;image.loading='lazy';imageLink.append(image);
      }
    }
    const copy = element('div', 'mtq-saved-product__copy');
    const heading = element('h2'); const title = element('a', '', product.title);title.href=productURL(product.handle);heading.append(title);
    const links = element('div', 'mtq-saved-product__links');
    const visit = element('a', '', label('view'));visit.href=productURL(product.handle);
    const remove = element('button', '', label('remove'));remove.type='button';remove.dataset.productHandle=product.handle;remove.dataset.mtqAction=shared?'remove-shared':`remove-${view}`;
    links.append(visit, remove);copy.append(heading, element('p', '', money(product.price)), links);card.append(imageLink, copy);return card;
  }
  comparison(container, products, shared) {
    const wrapper = element('div', 'mtq-compare-scroll');const table=element('table','mtq-compare-table');
    const body=element('tbody');const top=element('tr');const title=element('th','','MT QAST');title.scope='row';top.append(title);
    for(const product of products){const cell=element('td');cell.append(this.card(product,'compare',shared));top.append(cell);}body.append(top);
    const rows = [
      ['price', (p) => money(p.price)],
      ['availability', (p) => label(p.available ? 'in_stock' : 'sold_out')],
      ['options', (p) => (p.options || []).map((o) => `${o.name}: ${(o.values || []).join(', ')}`).join(' · ')],
      ['material', (p) => (Array.isArray(p.tags) ? p.tags : []).filter((t) => t.startsWith('material-')).map((t) => t.replace('material-', '').replace(/-/g, ' ')).join(', ') || '—']
    ];
    for(const [key,value] of rows){const row=element('tr');const header=element('th','',label(key));header.scope='row';row.append(header);for(const product of products)row.append(element('td','',value(product)));body.append(row);}
    table.append(body);wrapper.append(table);container.append(wrapper);
  }
}

const catalogueTagCache = new Map();

class MTQTagFilters extends Component {
  controller;
  connectedCallback() {
    super.connectedCallback();
    this.controller?.abort();
    this.controller = new AbortController();
    this.prepare();
  }
  updatedCallback() { super.updatedCallback(); this.prepare(); }
  disconnectedCallback() { super.disconnectedCallback(); this.controller?.abort(); }
  async prepare() {
    const base = this.dataset.url;
    if (!base || !this.dataset.handle) return;
    if (!this.querySelector('.mtq-tag-group')) {
      try {
        let tags = catalogueTagCache.get(base);
        if (!tags) {
          tags = new Set();
          for (let page = 1; page <= 10; page++) {
            const url = new URL(`${base.replace(/\/$/, '')}/products.json`, window.location.origin);
            url.searchParams.set('limit', '250'); url.searchParams.set('page', String(page));
            const response = await fetch(url.href, { signal: this.controller.signal, headers: { Accept: 'application/json' } });
            if (!response.ok) return;
            const data = await response.json();
            if (!Array.isArray(data.products)) return;
            for (const product of data.products) {
              const values = Array.isArray(product.tags) ? product.tags : String(product.tags || '').split(',');
              for (const value of values) tags.add(String(value).trim());
            }
            if (data.products.length < 250) break;
          }
          catalogueTagCache.set(base, tags);
        }
        if (!this.isConnected || this.controller.signal.aborted) return;
        for (const template of this.querySelectorAll('template[data-mtq-filter-template]')) {
          const fragment = template.content.cloneNode(true);
          for (const option of fragment.querySelectorAll('[data-mtq-tag]')) if (!tags.has(option.dataset.mtqTag)) option.remove();
          if (fragment.querySelector('[data-mtq-tag]')) this.append(fragment);
        }
      } catch { return; }
    }
    const path = window.location.pathname;
    const normalized = new URL(base, window.location.origin).pathname.replace(/\/$/, '');
    const current = path.startsWith(`${normalized}/`) ? decodeURIComponent(path.slice(normalized.length + 1)).split('+').filter((tag) => HANDLE.test(tag)) : [];
    for (const option of this.querySelectorAll('a[data-mtq-tag]')) {
      const tag = option.dataset.mtqTag;
      const prefix = option.dataset.mtqPrefix;
      const selected = current.includes(tag);
      const next = current.filter((value) => !value.startsWith(prefix));
      if (!selected) next.push(tag);
      const url = new URL(base, window.location.origin);
      if (next.length) url.pathname = `${url.pathname.replace(/\/$/, '')}/${next.map(encodeURIComponent).join('+')}`;
      url.search = window.location.search; url.searchParams.delete('page');
      option.href = url.href;
      option.classList.toggle('is-selected', selected);
      if (selected) option.setAttribute('aria-current', 'true'); else option.removeAttribute('aria-current');
      const box = option.querySelector('.mtq-tag-option__box'); if (box) box.textContent = selected ? '✓' : '';
    }
    if (current.length && !this.querySelector('.mtq-tag-clear')) {
      const clear = element('a', 'mtq-tag-clear', this.dataset.clearLabel);
      clear.href = base; this.append(clear);
    }
  }
}

if (!customElements.get('mtq-tools')) customElements.define('mtq-tools', MTQTools);
if (!customElements.get('mtq-catalog-page')) customElements.define('mtq-catalog-page', MTQCatalogPage);
if (!customElements.get('mtq-tag-filters')) customElements.define('mtq-tag-filters', MTQTagFilters);
